import { useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import IntegrationMappings from "../../components/integrations/IntegrationMappings";
import IntegrationUrlRules from "../../components/integrations/IntegrationUrlRules";
import OfficeKeysEditor from "../../components/integrations/OfficeKeysEditor";
import {
  CompanyType,
  IntegrationConfig,
  companyIntegrations,
  companyServices,
  integrationPollableEntityTypes,
  normalizableEntityTypes,
} from "../../models/company";
import IntegrationActivationPipeline from "./IntegrationActivationPipeline";

interface CompanyIntegrationsProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  selectedIntegration: string;
  activeIntegrations: string[];
  availableIntegrations: string[];
  integrationStatuses: string[];
  hasIntegrationChanges: (integrationName: string) => boolean;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  handleIntegrationSelect: (integrationType: string) => void;
  handleAddIntegration: (integrationName: string) => void;
  handleIntegrationStatusChange: (integrationName: string, status: string) => void;
  handleIntegrationConfigChange: (integrationName: string, path: string[], value: any) => void;
  onDelete: () => void;
  onStartActivationPipeline?: (integrationName: string, force?: boolean) => void;
  isPipelineStarting?: boolean;
  onPoll?: (integrationName: string, startTime?: string, entityTypes?: string[]) => void;
  isPolling?: boolean;
  canPoll?: boolean;
  onNormalize?: (integrationName: string, startTime?: string, entityTypes?: string[]) => void;
  isNormalizing?: boolean;
  canNormalize?: boolean;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
  detailLoading?: boolean;
  onOfficeKeysErrorChange?: (error: string | null) => void;
  officeKeysResetKey?: number;
}

function CompanyIntegrations({
  company,
  originalCompany,
  selectedIntegration,
  activeIntegrations,
  availableIntegrations,
  integrationStatuses,
  hasIntegrationChanges,
  hasFieldChanged,
  handleIntegrationSelect,
  handleAddIntegration,
  handleIntegrationStatusChange,
  handleIntegrationConfigChange,
  onDelete,
  onStartActivationPipeline,
  isPipelineStarting = false,
  onPoll,
  isPolling = false,
  canPoll = false,
  onNormalize,
  isNormalizing = false,
  canNormalize = false,
  ChangedIndicator,
  readOnly = false,
  detailLoading = false,
  onOfficeKeysErrorChange,
  officeKeysResetKey,
}: CompanyIntegrationsProps) {
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const [integrationTab, setIntegrationTab] = useState<string>("configuration");
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollStartTime, setPollStartTime] = useState<string>("");
  const [pollEntityTypes, setPollEntityTypes] = useState<string[]>([]);
  const [showNormalizeModal, setShowNormalizeModal] = useState(false);
  const [normalizeStartTime, setNormalizeStartTime] = useState<string>("");
  const [normalizeEntityTypes, setNormalizeEntityTypes] = useState<string[]>([]);

  const pollableEntityTypes = integrationPollableEntityTypes[selectedIntegration] ?? [];

  // Start of today (local time) in datetime-local format (YYYY-MM-DDTHH:mm).
  const startOfTodayLocal = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${startOfToday.getFullYear()}-${pad(startOfToday.getMonth() + 1)}-${pad(startOfToday.getDate())}T00:00`;
  };

  const openPollModal = () => {
    setPollStartTime(startOfTodayLocal());
    setPollEntityTypes([...pollableEntityTypes]);
    setShowPollModal(true);
  };

  const togglePollEntityType = (entityType: string) => {
    setPollEntityTypes((prev) =>
      prev.includes(entityType) ? prev.filter((t) => t !== entityType) : [...prev, entityType],
    );
  };

  const allPollEntityTypesSelected = pollEntityTypes.length === pollableEntityTypes.length;

  const toggleAllPollEntityTypes = () => {
    setPollEntityTypes(allPollEntityTypesSelected ? [] : [...pollableEntityTypes]);
  };

  const handleConfirmPoll = () => {
    // datetime-local yields a value like "2026-07-14T09:00"; send as ISO-8601.
    const startTime = pollStartTime ? new Date(pollStartTime).toISOString() : undefined;
    onPoll?.(selectedIntegration, startTime, pollEntityTypes);
    setShowPollModal(false);
  };

  const openNormalizeModal = () => {
    setNormalizeStartTime(startOfTodayLocal());
    setNormalizeEntityTypes([...normalizableEntityTypes]);
    setShowNormalizeModal(true);
  };

  const toggleNormalizeEntityType = (entityType: string) => {
    setNormalizeEntityTypes((prev) =>
      prev.includes(entityType) ? prev.filter((t) => t !== entityType) : [...prev, entityType],
    );
  };

  const allNormalizeEntityTypesSelected = normalizeEntityTypes.length === normalizableEntityTypes.length;

  const toggleAllNormalizeEntityTypes = () => {
    setNormalizeEntityTypes(allNormalizeEntityTypesSelected ? [] : [...normalizableEntityTypes]);
  };

  const handleConfirmNormalize = () => {
    const startTime = normalizeStartTime ? new Date(normalizeStartTime).toISOString() : undefined;
    onNormalize?.(selectedIntegration, startTime, normalizeEntityTypes);
    setShowNormalizeModal(false);
  };

  const handleAdd = (integrationName: string) => {
    handleAddIntegration(integrationName);
    setShowAddIntegrationModal(false);
  };

  const handleIntegrationSelectAndResetTab = (integrationType: string) => {
    setIntegrationTab("configuration");
    handleIntegrationSelect(integrationType);
  };

  // Auto-clear credential field on first focus
  const handleCredentialFocus = (integrationName: string, fieldName: string) => {
    const integrationConfig = (company?.integrations?.[integrationName] || {}) as IntegrationConfig;
    const originalIntegrationConfig = (originalCompany?.integrations?.[integrationName] || {}) as IntegrationConfig;
    const currentValue = integrationConfig[fieldName];
    const originalValue = originalIntegrationConfig[fieldName];

    // Only clear if field has a value AND hasn't been changed yet
    if (currentValue && !hasFieldChanged(currentValue, originalValue)) {
      handleIntegrationConfigChange(integrationName, [fieldName], null);
    }
  };

  // Reset/remove credential field
  const handleCredentialReset = (integrationName: string, fieldName: string) => {
    const integrationConfig = (company?.integrations?.[integrationName] || {}) as IntegrationConfig;
    const originalIntegrationConfig = (originalCompany?.integrations?.[integrationName] || {}) as IntegrationConfig;
    const currentValue = integrationConfig[fieldName];
    const originalValue = originalIntegrationConfig[fieldName];

    // If field hasn't been changed, remove it (set to null)
    // If field has been changed, restore to original value
    if (!hasFieldChanged(currentValue, originalValue)) {
      handleIntegrationConfigChange(integrationName, [fieldName], null);
    } else {
      handleIntegrationConfigChange(integrationName, [fieldName], originalValue ?? null);
    }
  };

  // Render credential field with password masking, auto-clear, and reset icon
  const renderCredentialField = (label: string, fieldName: string, placeholder: string) => {
    const integrationConfig = (company?.integrations?.[selectedIntegration] || {}) as IntegrationConfig;
    const originalIntegrationConfig = (originalCompany?.integrations?.[selectedIntegration] || {}) as IntegrationConfig;
    const currentValue = integrationConfig[fieldName] || "";
    const originalValue = originalIntegrationConfig[fieldName] || "";
    const isChanged = hasFieldChanged(currentValue, originalValue);

    return (
      <Form.Group className="mb-4">
        <Form.Label>
          {label}
          {isChanged && <ChangedIndicator />}
        </Form.Label>
        <InputGroup>
          <Form.Control
            type="password"
            value={currentValue}
            onChange={(e) => handleIntegrationConfigChange(selectedIntegration, [fieldName], e.target.value)}
            onFocus={() => handleCredentialFocus(selectedIntegration, fieldName)}
            placeholder={placeholder}
            readOnly={readOnly}
          />
          {!readOnly && (
            <InputGroup.Text
              as="button"
              type="button"
              className="border-start-0"
              style={{
                background: "white",
                cursor: "pointer",
                border: "1px solid #dee2e6",
                borderLeft: "none",
              }}
              onClick={() => handleCredentialReset(selectedIntegration, fieldName)}
              title={isChanged ? `Reset ${label.toLowerCase()} to original value` : `Remove ${label.toLowerCase()}`}
            >
              <i className="bi bi-trash text-danger"></i>
            </InputGroup.Text>
          )}
        </InputGroup>
      </Form.Group>
    );
  };

  return (
    <>
      <Container fluid>
        <Row>
          <Col md={3} className="border-end">
            <div className="list-group">
              {activeIntegrations.map((integrationName) => {
                const integrationConfig = (company?.integrations?.[integrationName] || {}) as IntegrationConfig;
                const status = integrationConfig.status || "inactive";
                return (
                  <button
                    key={integrationName}
                    type="button"
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedIntegration === integrationName ? "active" : ""}`}
                    onClick={() => handleIntegrationSelectAndResetTab(integrationName)}
                  >
                    <span className="text-capitalize">
                      {integrationName}
                      {hasIntegrationChanges(integrationName) && <ChangedIndicator />}
                    </span>
                    <span
                      className={`badge ${
                        status === "active" ? "bg-success" : status === "onboarding" ? "bg-warning" : "bg-secondary"
                      }`}
                    >
                      {status}
                    </span>
                  </button>
                );
              })}
              {availableIntegrations.length > 0 && !readOnly && (
                <button
                  type="button"
                  className="list-group-item list-group-item-action text-center"
                  onClick={() => setShowAddIntegrationModal(true)}
                  style={{ color: "#0d6efd", fontWeight: "500" }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Integration
                </button>
              )}
            </div>
          </Col>
          <Col md={9}>
            {detailLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                <div className="spinner-border text-secondary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              selectedIntegration &&
              (() => {
                const integrationConfig = (company?.integrations?.[selectedIntegration] || {}) as IntegrationConfig;
                const originalIntegrationConfig = (originalCompany?.integrations?.[selectedIntegration] ||
                  {}) as IntegrationConfig;
                const currentStatus = integrationConfig.status || "inactive";
                const pipelineConfig = [...companyIntegrations, ...companyServices].find(
                  (c) => c.id === selectedIntegration,
                );

                // Check for DNS records that need to be configured (Sendgrid specific)
                const domainValidationStep =
                  selectedIntegration === "sendgrid"
                    ? integrationConfig.validation?.steps.find((step) => step.step === "domain_validation")
                    : null;
                const dnsRecords = domainValidationStep?.data?.dns_records;

                return (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="text-capitalize mb-0">{selectedIntegration} Configuration</h4>
                      {!readOnly && (
                        <div className="d-flex gap-2">
                          {canPoll && pollableEntityTypes.length > 0 && (
                            <Button variant="outline-primary" onClick={openPollModal} size="sm">
                              <i className="bi bi-arrow-repeat me-2"></i>
                              Poll
                            </Button>
                          )}
                          {canNormalize && pollableEntityTypes.length > 0 && (
                            <Button variant="outline-primary" onClick={openNormalizeModal} size="sm">
                              <i className="bi bi-shuffle me-2"></i>
                              Normalize
                            </Button>
                          )}
                          <Button variant="outline-danger" onClick={onDelete} size="sm">
                            <i className="bi bi-trash me-2"></i>
                            Delete Integration
                          </Button>
                        </div>
                      )}
                    </div>

                    {selectedIntegration === "sendgrid" && currentStatus !== "active" && (
                      <Alert variant="danger" className="mb-4">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        This integration is not active. Test mails will be sent out via the Colibry sendgrid account
                        (domain colibry.ai).
                      </Alert>
                    )}

                    {/* DNS Records Alert for Sendgrid */}
                    {dnsRecords && Object.keys(dnsRecords).length > 0 && (
                      <Alert variant="danger" className="mb-4">
                        <Alert.Heading>
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          DNS Records Configuration Required
                        </Alert.Heading>
                        <p className="mb-3">The following DNS records need to be created for domain validation:</p>
                        {Object.entries(dnsRecords).map(([domain, records]: [string, any]) => (
                          <div key={domain} className="mb-3">
                            <h6 className="text-uppercase mb-2">{domain}</h6>
                            <Table bordered size="sm" className="mb-0 bg-white">
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Host</th>
                                  <th>Data</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(records).map(([recordName, record]: [string, any]) => (
                                  <tr key={recordName}>
                                    <td>
                                      <code>{record.type}</code>
                                    </td>
                                    <td>
                                      <code>{record.host}</code>
                                    </td>
                                    <td>
                                      <code className="text-break">{record.data}</code>
                                    </td>
                                    <td>
                                      {record.valid !== undefined &&
                                        (record.valid ? (
                                          <span className="text-success">
                                            <i className="bi bi-check-circle-fill me-1"></i>
                                            Valid
                                          </span>
                                        ) : (
                                          <span className="text-danger">
                                            <i className="bi bi-x-circle-fill me-1"></i>
                                            Pending
                                          </span>
                                        ))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ))}
                      </Alert>
                    )}

                    <Row>
                      <Col lg={pipelineConfig?.hasPipeline ? 6 : 12}>
                        {(integrationConfig.validation?.completed_at ||
                          integrationConfig.validation?.current_step === "pending_for_activation") &&
                          currentStatus !== "active" && (
                            <Alert variant="warning" className="mb-3 py-2">
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>
                              This integration is ready to be activated.
                            </Alert>
                          )}

                        <div className="d-flex gap-3 mb-4">
                          <Form.Group className="flex-grow-1">
                            <Form.Label>
                              Status
                              {hasFieldChanged(integrationConfig.status, originalIntegrationConfig.status) && (
                                <ChangedIndicator />
                              )}
                            </Form.Label>
                            <Form.Select
                              value={currentStatus}
                              onChange={(e) => handleIntegrationStatusChange(selectedIntegration, e.target.value)}
                              disabled={readOnly}
                            >
                              {integrationStatuses
                                .filter((status) => {
                                  if (!pipelineConfig?.hasPipeline || status !== "active") return true;
                                  if (currentStatus === "active" || originalIntegrationConfig.status === "active")
                                    return true;
                                  return integrationConfig.validation?.current_step === "pending_for_activation";
                                })
                                .map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                            </Form.Select>
                          </Form.Group>
                          {currentStatus === "onboarding" && pipelineConfig?.autoActivateCanOverride && (
                            <Form.Group className="text-nowrap text-center">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    When enabled, the integration will be automatically activated once all validation
                                    steps complete successfully.
                                  </Tooltip>
                                }
                              >
                                <Form.Label style={{ cursor: "help" }}>
                                  Auto Activate <i className="bi bi-info-circle"></i>
                                  {hasFieldChanged(
                                    integrationConfig.auto_activate,
                                    originalIntegrationConfig.auto_activate,
                                  ) && <ChangedIndicator />}
                                </Form.Label>
                              </OverlayTrigger>
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <Form.Check
                                  type="switch"
                                  id="auto-activate-toggle"
                                  checked={
                                    integrationConfig.auto_activate ?? pipelineConfig?.autoActivateDefault ?? false
                                  }
                                  onChange={(e) =>
                                    handleIntegrationConfigChange(
                                      selectedIntegration,
                                      ["auto_activate"],
                                      e.target.checked,
                                    )
                                  }
                                  disabled={readOnly}
                                  style={{ transform: "scale(1.5)" }}
                                />
                              </div>
                            </Form.Group>
                          )}
                        </div>

                        {currentStatus !== "inactive" && (
                          <>
                            {selectedIntegration === "whise" && (
                              <Tabs
                                activeKey={integrationTab}
                                onSelect={(k) => setIntegrationTab(k ?? "configuration")}
                                className="mb-4"
                              >
                                <Tab eventKey="configuration" title="Configuration">
                                  <Form.Group className="mb-4">
                                    <Form.Label>
                                      Client ID
                                      {hasFieldChanged(
                                        integrationConfig.client_id,
                                        originalIntegrationConfig.client_id,
                                      ) && <ChangedIndicator />}
                                    </Form.Label>
                                    <Form.Control
                                      type="number"
                                      value={integrationConfig.client_id || ""}
                                      onChange={(e) =>
                                        handleIntegrationConfigChange(
                                          selectedIntegration,
                                          ["client_id"],
                                          parseInt(e.target.value) || "",
                                        )
                                      }
                                      placeholder="Enter client ID"
                                      readOnly={readOnly}
                                    />
                                  </Form.Group>
                                </Tab>
                                <Tab eventKey="mappings" title="Mappings" mountOnEnter>
                                  <IntegrationMappings
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="whise"
                                  />
                                </Tab>
                                <Tab eventKey="urls" title="URLs" mountOnEnter>
                                  <IntegrationUrlRules
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="whise"
                                  />
                                </Tab>
                              </Tabs>
                            )}

                            {selectedIntegration === "pipedrive" && (
                              <>
                                <Form.Group className="mb-4">
                                  <Form.Label>
                                    API Name
                                    {hasFieldChanged(
                                      integrationConfig.api_name,
                                      originalIntegrationConfig.api_name,
                                    ) && <ChangedIndicator />}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={integrationConfig.api_name || ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(selectedIntegration, ["api_name"], e.target.value)
                                    }
                                    placeholder="Enter API name"
                                    readOnly={readOnly}
                                  />
                                </Form.Group>

                                {renderCredentialField("API Token", "api_token", "Enter API token")}
                              </>
                            )}

                            {selectedIntegration === "zabun" && (
                              <Tabs
                                activeKey={integrationTab}
                                onSelect={(k) => setIntegrationTab(k ?? "configuration")}
                                className="mb-4"
                              >
                                <Tab eventKey="configuration" title="Configuration">
                                  <Form.Group className="mb-4">
                                    <Form.Label>
                                      X-Client ID
                                      {hasFieldChanged(
                                        integrationConfig.x_client_id,
                                        originalIntegrationConfig.x_client_id,
                                      ) && <ChangedIndicator />}
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={integrationConfig.x_client_id || ""}
                                      onChange={(e) =>
                                        handleIntegrationConfigChange(
                                          selectedIntegration,
                                          ["x_client_id"],
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Enter X-Client ID"
                                      readOnly={readOnly}
                                    />
                                  </Form.Group>

                                  {renderCredentialField("Client ID", "client_id", "Enter client ID")}
                                  {renderCredentialField("Server ID", "server_id", "Enter server ID")}
                                  {renderCredentialField("API Key", "api_key", "Enter API key")}
                                </Tab>
                                <Tab eventKey="mappings" title="Mappings" mountOnEnter>
                                  <IntegrationMappings
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="zabun"
                                  />
                                </Tab>
                                <Tab eventKey="urls" title="URLs" mountOnEnter>
                                  <IntegrationUrlRules
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="zabun"
                                  />
                                </Tab>
                              </Tabs>
                            )}

                            {selectedIntegration === "zabun360" && (
                              <Tabs
                                activeKey={integrationTab}
                                onSelect={(k) => setIntegrationTab(k ?? "configuration")}
                                className="mb-4"
                              >
                                <Tab eventKey="configuration" title="Configuration">
                                  <OfficeKeysEditor
                                    key={officeKeysResetKey}
                                    value={integrationConfig.api_key}
                                    originalValue={originalIntegrationConfig.api_key}
                                    onChange={(officeKeys) =>
                                      handleIntegrationConfigChange(selectedIntegration, ["api_key"], officeKeys)
                                    }
                                    onErrorChange={onOfficeKeysErrorChange}
                                    readOnly={readOnly}
                                    ChangedIndicator={ChangedIndicator}
                                  />

                                  <Form.Group className="mb-4">
                                    <Form.Label>
                                      Culture
                                      {hasFieldChanged(
                                        integrationConfig.culture,
                                        originalIntegrationConfig.culture,
                                      ) && <ChangedIndicator />}
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={integrationConfig.culture ?? "nl-BE"}
                                      onChange={(e) =>
                                        handleIntegrationConfigChange(selectedIntegration, ["culture"], e.target.value)
                                      }
                                      placeholder="nl-BE"
                                      readOnly={readOnly}
                                    />
                                    <Form.Text className="text-muted">
                                      BCP-47 Accept-Language tag sent on all requests (e.g. nl-BE, fr-BE, en-US).
                                    </Form.Text>
                                  </Form.Group>
                                </Tab>
                                <Tab eventKey="mappings" title="Mappings" mountOnEnter>
                                  <IntegrationMappings
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="zabun360"
                                  />
                                </Tab>
                                <Tab eventKey="urls" title="URLs" mountOnEnter>
                                  <IntegrationUrlRules
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="zabun360"
                                  />
                                </Tab>
                              </Tabs>
                            )}

                            {selectedIntegration === "omnicasa" && (
                              <Tabs
                                activeKey={integrationTab}
                                onSelect={(k) => setIntegrationTab(k ?? "configuration")}
                                className="mb-4"
                              >
                                <Tab eventKey="configuration" title="Configuration">
                                  {renderCredentialField("Customer Name", "customer_name", "Enter customer name")}
                                  {renderCredentialField(
                                    "Customer Password",
                                    "customer_password",
                                    "Enter customer password",
                                  )}
                                </Tab>
                                <Tab eventKey="mappings" title="Mappings" mountOnEnter>
                                  <IntegrationMappings
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="omnicasa"
                                  />
                                </Tab>
                                <Tab eventKey="urls" title="URLs" mountOnEnter>
                                  <IntegrationUrlRules
                                    companyNameOrId={(company.id ?? company.name) as string}
                                    integrationName="omnicasa"
                                  />
                                </Tab>
                              </Tabs>
                            )}

                            {selectedIntegration === "dotdigital" && (
                              <>
                                {renderCredentialField("API Key", "api_key", "Enter API key")}

                                <Form.Group className="mb-4">
                                  <Form.Label>
                                    Template ID
                                    {hasFieldChanged(
                                      integrationConfig.template_id,
                                      originalIntegrationConfig.template_id,
                                    ) && <ChangedIndicator />}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={integrationConfig.template_id || ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(
                                        selectedIntegration,
                                        ["template_id"],
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter template ID"
                                    readOnly={readOnly}
                                  />
                                </Form.Group>
                              </>
                            )}

                            {selectedIntegration === "sendgrid" && (
                              <>
                                {renderCredentialField("API Key", "api_key", "Enter API key")}

                                <Form.Group className="mb-4">
                                  <Form.Label>
                                    Domain Transactional
                                    {hasFieldChanged(
                                      integrationConfig.domain_transactional,
                                      originalIntegrationConfig.domain_transactional,
                                    ) && <ChangedIndicator />}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={integrationConfig.domain_transactional || ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(
                                        selectedIntegration,
                                        ["domain_transactional"],
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter transactional domain"
                                    readOnly={readOnly}
                                  />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                  <Form.Label>
                                    Domain Marketing
                                    {hasFieldChanged(
                                      integrationConfig.domain_marketing,
                                      originalIntegrationConfig.domain_marketing,
                                    ) && <ChangedIndicator />}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={integrationConfig.domain_marketing || ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(
                                        selectedIntegration,
                                        ["domain_marketing"],
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter marketing domain"
                                    readOnly={readOnly}
                                  />
                                </Form.Group>
                              </>
                            )}

                            {selectedIntegration === "smstools" && (
                              <>
                                <Form.Group className="mb-4">
                                  <Form.Label>
                                    Subaccount ID
                                    {hasFieldChanged(
                                      integrationConfig.subaccount_id,
                                      originalIntegrationConfig.subaccount_id,
                                    ) && <ChangedIndicator />}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={integrationConfig.subaccount_id || ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(
                                        selectedIntegration,
                                        ["subaccount_id"],
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter subaccount ID"
                                    readOnly={readOnly}
                                  />
                                </Form.Group>
                                <Form.Group className="mb-4">
                                  <Form.Label>
                                    Sender Name{" "}
                                    <OverlayTrigger
                                      placement="right"
                                      overlay={
                                        <Tooltip>
                                          This name may be used as the sender ID in SMSes sent to countries that support
                                          a sender ID.
                                        </Tooltip>
                                      }
                                    >
                                      <i className="bi bi-info-circle" />
                                    </OverlayTrigger>
                                    {hasFieldChanged(
                                      integrationConfig.sender_name,
                                      originalIntegrationConfig.sender_name,
                                    ) && <ChangedIndicator />}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={integrationConfig.sender_name || ""}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 11);
                                      handleIntegrationConfigChange(selectedIntegration, ["sender_name"], value);
                                    }}
                                    placeholder="Enter sender name"
                                    maxLength={11}
                                    readOnly={readOnly}
                                  />
                                  <Form.Text className="text-muted">
                                    Max 11 alphanumeric characters, no spaces.
                                  </Form.Text>
                                </Form.Group>
                              </>
                            )}

                            {["hubspot", "skarabee", "salesforce"].includes(selectedIntegration) && (
                              <div className="alert alert-info">
                                No additional configuration required for this integration beyond status.
                              </div>
                            )}
                          </>
                        )}
                      </Col>

                      {pipelineConfig?.hasPipeline && (
                        <Col lg={6}>
                          <IntegrationActivationPipeline
                            validation={integrationConfig.validation}
                            hasPipeline={pipelineConfig.hasPipeline}
                            integrationName={selectedIntegration}
                            integrationStatus={currentStatus}
                            onStartPipeline={(force) => onStartActivationPipeline?.(selectedIntegration, force)}
                            isPipelineStarting={isPipelineStarting}
                            readOnly={readOnly}
                          />
                        </Col>
                      )}
                    </Row>
                  </div>
                );
              })()
            )}
          </Col>
        </Row>
      </Container>

      {/* Add Integration Modal */}
      <Modal show={showAddIntegrationModal} onHide={() => setShowAddIntegrationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select an integration to add:</p>
          <div className="list-group">
            {availableIntegrations.map((integrationName) => (
              <button
                key={integrationName}
                type="button"
                className="list-group-item list-group-item-action text-capitalize"
                onClick={() => handleAdd(integrationName)}
              >
                {integrationName}
              </button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddIntegrationModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Poll Modal */}
      <Modal show={showPollModal} onHide={() => setShowPollModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-capitalize">Poll {selectedIntegration}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>Start Time (optional)</Form.Label>
            <InputGroup>
              <Form.Control
                type="datetime-local"
                value={pollStartTime}
                onChange={(e) => setPollStartTime(e.target.value)}
              />
              <Button variant="outline-secondary" onClick={() => setPollStartTime("")} disabled={!pollStartTime}>
                Clear
              </Button>
            </InputGroup>
            <Form.Text className="text-muted">
              When cleared, each entity type resolves its own start time from the latest stored record.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-0">Entity Types</Form.Label>
              <Button variant="link" size="sm" className="p-0" onClick={toggleAllPollEntityTypes}>
                {allPollEntityTypesSelected ? "Deselect all" : "Select all"}
              </Button>
            </div>
            <Form.Text className="text-muted d-block mb-2">Select at least one entity type.</Form.Text>
            {pollableEntityTypes.map((entityType) => (
              <Form.Check
                key={entityType}
                type="checkbox"
                id={`poll-entity-${entityType}`}
                label={entityType}
                checked={pollEntityTypes.includes(entityType)}
                onChange={() => togglePollEntityType(entityType)}
              />
            ))}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPollModal(false)} disabled={isPolling}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmPoll} disabled={isPolling || pollEntityTypes.length === 0}>
            {isPolling ? "Polling…" : "Poll"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Normalize Modal */}
      <Modal show={showNormalizeModal} onHide={() => setShowNormalizeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-capitalize">Normalize {selectedIntegration}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>Start Time (optional)</Form.Label>
            <InputGroup>
              <Form.Control
                type="datetime-local"
                value={normalizeStartTime}
                onChange={(e) => setNormalizeStartTime(e.target.value)}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setNormalizeStartTime("")}
                disabled={!normalizeStartTime}
              >
                Clear
              </Button>
            </InputGroup>
            <Form.Text className="text-muted">
              Only raw records updated in the CRM since this time are normalized. When cleared, all raw records are
              normalized.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-0">Entity Types</Form.Label>
              <Button variant="link" size="sm" className="p-0" onClick={toggleAllNormalizeEntityTypes}>
                {allNormalizeEntityTypesSelected ? "Deselect all" : "Select all"}
              </Button>
            </div>
            <Form.Text className="text-muted d-block mb-2">Select at least one entity type.</Form.Text>
            {normalizableEntityTypes.map((entityType) => (
              <Form.Check
                key={entityType}
                type="checkbox"
                id={`normalize-entity-${entityType}`}
                label={entityType}
                checked={normalizeEntityTypes.includes(entityType)}
                onChange={() => toggleNormalizeEntityType(entityType)}
              />
            ))}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNormalizeModal(false)} disabled={isNormalizing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmNormalize}
            disabled={isNormalizing || normalizeEntityTypes.length === 0}
          >
            {isNormalizing ? "Normalizing…" : "Normalize"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CompanyIntegrations;
